import { useSelector } from "react-redux";
import { format_date } from "../../Utils/helpers";
import { getSenderId } from "../../Utils/chatSocket";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { sendMessage } from "../../Services/Chat";
import { Error as ErrorAlert } from "../../Components/Modal";
import Button from "../../Components/Button";

const MAX_MESSAGE_LENGTH = 1000;

const isSameDay = (a, b) =>
  a && b && new Date(a).toDateString() === new Date(b).toDateString();

export default function Message({
  data,
  chatTab,
  status,
  loading,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  onSent,
}) {
  const { user } = useSelector((state) => state?.slice);
  const [message, setMessage] = useState("");
  const listRef = useRef(null);
  const prevFirstIdRef = useRef(null);
  const prevLastIdRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const adminId = user?._id?.toString();
  const participantId = chatTab?.toString();
  const isOpen = status === "pending" || status === "active";

  const { mutate, isLoading: sending } = useMutation(
    (payload) => sendMessage(payload),
    {
      onSuccess: (res) => {
        const newMsg = res?.data?.message;
        if (newMsg) onSent?.(newMsg, status !== "active");
        setMessage("");
      },
      onError: (err) => {
        ErrorAlert(err?.response?.data?.message || "Message could not be sent.");
      },
    }
  );

  const firstId = data?.[0]?._id;
  const lastId = data?.[data.length - 1]?._id;

  // Older page prepended: keep the reader where they were. New message appended: jump to the bottom.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const prependedOlder =
      prevFirstIdRef.current &&
      firstId !== prevFirstIdRef.current &&
      lastId === prevLastIdRef.current;

    if (prependedOlder) {
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current + el.scrollTop;
    } else if (lastId !== prevLastIdRef.current) {
      el.scrollTop = el.scrollHeight;
    }

    prevFirstIdRef.current = firstId;
    prevLastIdRef.current = lastId;
    prevScrollHeightRef.current = el.scrollHeight;
  }, [firstId, lastId]);

  useEffect(() => {
    setMessage("");
  }, [participantId]);

  const handleLoadOlder = () => {
    if (listRef.current) prevScrollHeightRef.current = listRef.current.scrollHeight;
    onLoadOlder?.();
  };

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed || !participantId || sending) return;
    mutate({ message: trimmed, receiverId: participantId });
  };

  let composerHint = "";
  if (status === "pending") composerHint = "Replying will accept this request and start the chat.";
  else if (!isOpen) composerHint = "This chat is closed. Sending a message starts a new chat with this person.";

  return (
    <div className="premium-chat-thread">
      <ul ref={listRef} className="premium-chat-messages custom-scrollbar">
        {hasOlder && (
          <li className="premium-chat-load-older">
            <button type="button" onClick={handleLoadOlder} disabled={loadingOlder}>
              {loadingOlder ? (
                <i className="fas fa-circle-notch fa-spin" />
              ) : (
                "Load older messages"
              )}
            </button>
          </li>
        )}

        {loading ? (
          <li className="premium-chat-state-inline">
            <i className="fas fa-circle-notch fa-spin fa-2x text-primary" />
          </li>
        ) : data?.length > 0 ? (
          data.map((item, index) => {
            const prev = data[index - 1];
            const showDate = !prev || !isSameDay(prev.createdAt, item.createdAt);
            const dateLabel = showDate && item?.createdAt && (
              <li key={`${item?._id}-date`} className="premium-chat-date">
                <span>{format_date(item.createdAt, "MMM DD, YYYY")}</span>
              </li>
            );

            if (item?.type === "system") {
              return [
                dateLabel,
                <li key={`${item?._id}-system`} className="premium-msg-system">
                  <span>
                    {item?.message}
                    {item?.createdAt && (
                      <time> · {format_date(item.createdAt, "hh:mm A")}</time>
                    )}
                  </span>
                </li>,
              ];
            }

            const isMe = adminId === getSenderId(item);
            const senderIsOtherAdmin = !isMe && item?.fromAdmin;
            const otherAdminName = senderIsOtherAdmin
              ? [item?.sender?.firstName, item?.sender?.lastName].filter(Boolean).join(" ")
              : "";
            return [
              dateLabel,
              <li
                key={`${item?._id}-message`}
                className={`premium-msg ${isMe || senderIsOtherAdmin ? "premium-msg-me" : "premium-msg-them"}`}
              >
                {senderIsOtherAdmin && otherAdminName && (
                  <span className="premium-msg-author">{otherAdminName}</span>
                )}
                <div className="premium-msg-bubble">{item?.message}</div>
                <time className="premium-msg-time">
                  {item?.createdAt && format_date(item.createdAt, "hh:mm A")}
                </time>
              </li>,
            ];
          })
        ) : (
          <li className="premium-chat-state-inline">
            <i className="fas fa-comments fa-2x mb-2 opacity-25" />
            <p>No messages yet. Send a message to start a chat.</p>
          </li>
        )}
      </ul>

      <footer className="premium-chat-composer">
        {composerHint && <p className="premium-chat-composer-hint">{composerHint}</p>}
        <div className="premium-chat-composer-inner">
          <textarea
            placeholder="Type your message..."
            value={message}
            maxLength={MAX_MESSAGE_LENGTH}
            onChange={({ target }) => setMessage(target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
          />
          <Button
            className="premium-chat-send-btn"
            onClick={handleSend}
            loading={sending}
            disabled={!participantId || !message.trim()}
          >
            <i className="fa fa-paper-plane" aria-hidden="true" />
          </Button>
        </div>
      </footer>
    </div>
  );
}
